"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Target, Eye, ArrowRight } from "lucide-react";
import * as LucideIcons from "lucide-react";
import Loader from "@/components/Loader";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchCoreValues } from "@/store/slices/landingSlice";
import Link from "next/link";

const ValuesSection = () => {
  const { mode } = useAppSelector((state) => state.theme);
  const dispatch = useAppDispatch();
  const { data: coreValues, loading } = useAppSelector(
    (state) => state.landing.coreValues,
  );

  useEffect(() => {
    dispatch(fetchCoreValues());
  }, [dispatch]);

  return (
    <section
      className={`py-24 relative overflow-hidden transition-colors duration-700 ${
        mode === "dark"
          ? "bg-[#0B0F19]"
          : "bg-gradient-to-b from-white via-slate-50 to-white"
      }`}
    >
      {/* Background (matches AboutHeroSection) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className={`absolute inset-0 transition-opacity duration-700 ${mode === "dark" ? "opacity-[0.05]" : "opacity-[0.04]"}`}
          style={{
            backgroundImage:
              mode === "dark"
                ? `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`
                : `linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />
        <motion.div
          className={`absolute -left-24 top-8 h-[420px] w-[420px] rounded-full blur-[110px] ${mode === "dark" ? "bg-[#8B31FF]/25" : "bg-[#8B31FF]/12"}`}
          animate={{ x: [0, 70, 0], y: [0, 25, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className={`absolute -right-20 bottom-0 h-[460px] w-[460px] rounded-full blur-[120px] ${mode === "dark" ? "bg-[#8B31FF]/18" : "bg-[#8B31FF]/10"}`}
          animate={{ x: [0, -80, 0], y: [0, 30, 0] }}
          transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Mission & Vision */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
          {/* Mission */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            whileHover={{ scale: 1.02 }}
            className="relative h-full"
          >
            <div
              className={`rounded-3xl cursor-pointer p-10 shadow-xl border relative overflow-hidden group h-full transition-all duration-500 ${
                mode === "dark"
                  ? "bg-[#1A1F2E] border-white/10"
                  : "bg-white border-gray-100"
              }`}
            >
              {/* Background gradient on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#8B31FF]/5 to-[#8B31FF]/5 opacity-0 group-hover:opacity-100 transition-opacity" />

              {/* Icon */}
              <motion.div
                whileHover={{ rotate: 360, scale: 1.1 }}
                transition={{ duration: 0.6 }}
                className="relative mb-6"
              >
                <div className="inline-flex p-5 rounded-lg bg-gradient-to-br from-[#8B31FF] to-[#8B31FF] shadow-lg">
                  <Target className="w-12 h-12 text-white" />
                </div>
              </motion.div>

              {/* Content */}
              <h3
                className={`text-3xl font-bold mb-4 relative transition-colors duration-500 ${mode === "dark" ? "text-white" : "text-gray-900"}`}
              >
                Our Mission
              </h3>
              <p
                className={`text-lg leading-relaxed relative transition-colors duration-500 ${mode === "dark" ? "text-gray-400" : "text-gray-700"}`}
              >
                To empower businesses worldwide with innovative digital
                solutions that drive growth, enhance efficiency, and create
                lasting value. We strive to be the trusted partner in every
                client&apos;s digital transformation journey.
              </p>

              {/* Decorative element */}
              <div className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r from-[#8B31FF] to-[#8B31FF]" />
            </div>
          </motion.div>

          {/* Vision */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            whileHover={{ scale: 1.02 }}
            className="relative h-full"
          >
            <div
              className={`rounded-3xl p-10 shadow-xl border relative overflow-hidden group h-full transition-all duration-500 ${
                mode === "dark"
                  ? "bg-[#1A1F2E] border-white/10"
                  : "bg-white border-gray-100"
              }`}
            >
              {/* Background gradient on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#8B31FF]/5 to-[#8B31FF]/5 opacity-0 group-hover:opacity-100 transition-opacity" />

              {/* Icon */}
              <motion.div
                whileHover={{ rotate: 360, scale: 1.1 }}
                transition={{ duration: 0.6 }}
                className="relative mb-6"
              >
                <div className="inline-flex p-5 rounded-lg bg-gradient-to-br from-[#8B31FF] to-[#8B31FF] shadow-lg">
                  <Eye className="w-12 h-12 text-white" />
                </div>
              </motion.div>

              {/* Content */}
              <h3
                className={`text-3xl font-bold mb-4 relative transition-colors duration-500 ${mode === "dark" ? "text-white" : "text-gray-900"}`}
              >
                Our Vision
              </h3>
              <p
                className={`text-lg leading-relaxed relative transition-colors duration-500 ${mode === "dark" ? "text-gray-400" : "text-gray-700"}`}
              >
                To be the global leader in digital innovation, recognized for
                transforming industries through technology excellence. We
                envision a future where every business, regardless of size, has
                access to world-class digital solutions.
              </p>

              {/* Decorative element */}
              <div className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r from-[#8B31FF] to-[#8B31FF]" />
            </div>
          </motion.div>
        </div>

        {/* Core Values Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className={`inline-block font-semibold text-sm uppercase tracking-wider mb-3 transition-colors duration-500 ${
              mode === "dark" ? "text-[#8B31FF]" : "text-[#8B31FF]"
            }`}
          >
            Core Values
          </motion.span>
          <h2
            className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-4 sm:mb-6 tracking-tight leading-[1.12] ${mode === "dark" ? "text-white" : "text-gray-900"}`}
          >
            What We{" "}
            <span className="bg-gradient-to-r from-[#8B31FF] to-[#8B31FF] bg-clip-text text-transparent">
              Stand For
            </span>
          </h2>
          <p
            className={`text-xl max-w-2xl mx-auto transition-colors duration-500 ${mode === "dark" ? "text-gray-400" : "text-gray-600"}`}
          >
            The principles that guide our decisions and shape our culture
          </p>
        </motion.div>

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <Loader
              loading={loading}
              loadingStates={[
                { text: "Loading core values" },
                { text: "Preparing value pillars" },
                { text: "Finalizing value cards" },
              ]}
              mode={mode}
              className="min-h-[220px]"
            />
          </div>
        ) : (
          <>
            {/* Core Values Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {coreValues.map((value, index) => {
                // @ts-ignore
                const Icon = LucideIcons[value.icon] || LucideIcons.Heart;

                return (
                  <Link href={"/contact"} key={value._id || index}>
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1, duration: 0.6 }}
                      whileHover={{ y: -10, scale: 1.02 }}
                      className="group relative h-full"
                    >
                      <div
                        className={`relative h-full cursor-pointer flex flex-col justify-between overflow-hidden rounded-[2rem] border p-10 backdrop-blur-md transition-all duration-500 ${
                          mode === "dark"
                            ? "bg-[#060B1A]/90 border-white/10 hover:border-[#8B31FF]/35 hover:shadow-[0_0_40px_rgba(34,211,238,0.12)]"
                            : "bg-white/95 border-[#8B31FF]/15 hover:border-[#8B31FF]/35 hover:shadow-[0_12px_32px_rgba(124,58,237,0.12)]"
                        }`}
                      >
                        <div className="absolute inset-0 bg-gradient-to-br from-[#8B31FF]/0 via-[#8B31FF]/6 to-[#8B31FF]/8 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                        <div
                          className={`absolute -right-20 -bottom-20 h-60 w-60 rounded-full blur-[90px] transition-all duration-500 group-hover:scale-110 ${mode === "dark" ? "bg-[#8B31FF]/12" : "bg-[#8B31FF]/10"}`}
                        />

                        <div className="absolute bottom-[-8%] right-[-8%] opacity-[0.05] transition-transform duration-700 group-hover:scale-110 group-hover:rotate-12 pointer-events-none">
                          <Icon
                            className={`h-56 w-56 ${mode === "dark" ? "text-[#8B31FF]" : "text-[#8B31FF]"}`}
                          />
                        </div>

                        <div className="relative z-10">
                          <div className="relative mb-8 inline-flex items-center justify-center">
                            <div
                              className={`absolute inset-0 rounded-full blur-xl ${mode === "dark" ? "bg-[#8B31FF]/25" : "bg-[#8B31FF]/15"}`}
                            />
                            <div
                              className={`relative flex h-20 w-20 items-center justify-center rounded-3xl border ${
                                mode === "dark"
                                  ? "bg-[#0B1224] border-[#8B31FF]/25"
                                  : "bg-white border-[#8B31FF]/20"
                              }`}
                            >
                              <Icon
                                className={`h-10 w-10 ${mode === "dark" ? "text-[#8B31FF]" : "text-[#8B31FF]"}`}
                              />
                            </div>
                          </div>

                          <h3
                            className={`mb-4 text-2xl font-bold leading-tight transition-colors duration-300 ${mode === "dark" ? "text-white group-hover:text-[#8B31FF]" : "text-gray-900 group-hover:text-[#8B31FF]"}`}
                          >
                            {value.title}
                          </h3>
                          <p
                            className={`mb-10 text-base leading-relaxed transition-colors duration-300 ${mode === "dark" ? "text-gray-400" : "text-gray-600"}`}
                          >
                            {value.description}
                          </p>
                        </div>

                        <div
                          className={`relative z-10 mt-auto flex items-center justify-between border-t pt-7 ${mode === "dark" ? "border-white/10" : "border-[#8B31FF]/12"}`}
                        >
                          <span
                            className={`text-sm font-semibold uppercase tracking-wide ${mode === "dark" ? "text-[#8B31FF]" : "text-[#8B31FF]"}`}
                          >
                            Learn More
                          </span>
                          <motion.div
                            whileHover={{ x: 5 }}
                            className={`flex h-14 w-14 items-center justify-center rounded-full border transition-all duration-300 ${
                              mode === "dark"
                                ? "bg-white/5 border-white/15 text-white group-hover:bg-[#8B31FF] group-hover:border-[#8B31FF] group-hover:text-black"
                                : "bg-slate-50 border-[#8B31FF]/20 text-[#8B31FF] group-hover:bg-[#8B31FF] group-hover:border-[#8B31FF] group-hover:text-white"
                            }`}
                          >
                            <ArrowRight className="h-6 w-6" />
                          </motion.div>
                        </div>
                      </div>
                    </motion.div>
                  </Link>
                );
              })}
            </div>

            {/* No Values Message */}
            {coreValues.length === 0 && !loading && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-12"
              >
                <p
                  className={
                    mode === "dark" ? "text-gray-500" : "text-gray-600 text-lg"
                  }
                >
                  No core values available at the moment.
                </p>
              </motion.div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default ValuesSection;
